import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  beginTelegramRealtimeConnection,
  getPortfolioThresholdSettings,
  getTelegramRealtimeStatus,
  setTelegramRealtimeActive,
  updateAllPortfolioThresholdSettings,
  updatePortfolioThresholdSettings,
  type PortfolioThresholdItem,
  type PortfolioThresholdSettings,
  type TelegramRealtimeStatus,
} from '../../api/portfolioAlerts';
import {
  disableWebPush,
  enableWebPush,
  getWebPushBrowserStatus,
  getWebPushServerStatus,
  syncExistingWebPushSubscription,
  type WebPushBrowserStatus,
  type WebPushServerStatus,
} from '../live/webPushClient';

type Props = {
  authenticated: boolean;
};

const EMPTY_SERVER: WebPushServerStatus = {
  serverConfigured: false,
  vapidPublicKey: '',
  subscribed: false,
  subscriptionCount: 0,
};

const EMPTY_BROWSER: WebPushBrowserStatus = {
  supported: false,
  permission: 'unsupported',
  subscribed: false,
};

const PANEL = '#ffffff';
const CARD = '#f8fafc';
const BORDER = '1px solid #e2e8f0';
const TXT = '#172033';
const SUB = '#596678';
const SUB2 = '#8793a2';
const BLUE = '#0b4f71';
const GOOD = '#2f7d4a';
const AMBER = '#a06a0a';

function normalizedDraft(item: PortfolioThresholdItem): PortfolioThresholdItem {
  return {
    ...item,
    upPct: Number.isFinite(item.upPct) ? Math.abs(item.upPct) : 5,
    downPct: Number.isFinite(item.downPct) ? -Math.abs(item.downPct) : -4,
    enabled: item.enabled !== false,
  };
}

function validPct(value: number) {
  return Number.isFinite(value) && value >= 0.1 && value <= 30;
}

export default function PortfolioThresholdAlertPanel({ authenticated }: Props) {
  const [settings, setSettings] = useState<PortfolioThresholdSettings | null>(null);
  const [drafts, setDrafts] = useState<PortfolioThresholdItem[]>([]);
  const [globalUpPct, setGlobalUpPct] = useState(5);
  const [globalDownPct, setGlobalDownPct] = useState(4);
  const [serverStatus, setServerStatus] = useState<WebPushServerStatus>(EMPTY_SERVER);
  const [browserStatus, setBrowserStatus] = useState<WebPushBrowserStatus>(EMPTY_BROWSER);
  const [telegram, setTelegram] = useState<TelegramRealtimeStatus | null>(null);
  const [telegramDeepLink, setTelegramDeepLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [channelAction, setChannelAction] = useState('');
  const [message, setMessage] = useState('');

  const refreshChannels = useCallback(async () => {
    const [initialServer, initialBrowser, telegramStatus] = await Promise.all([
      getWebPushServerStatus(),
      getWebPushBrowserStatus(),
      getTelegramRealtimeStatus().catch(() => null),
    ]);

    let server = initialServer;
    if (
      initialServer.serverConfigured
      && initialBrowser.permission === 'granted'
      && initialBrowser.subscribed
      && !initialServer.subscribed
    ) {
      await syncExistingWebPushSubscription();
      server = await getWebPushServerStatus();
    }

    setServerStatus(server);
    setBrowserStatus(initialBrowser);
    setTelegram(telegramStatus);
  }, []);

  const refresh = useCallback(async () => {
    if (!authenticated) return;
    setLoading(true);
    setMessage('');
    try {
      const [nextSettings] = await Promise.all([
        getPortfolioThresholdSettings(),
        refreshChannels(),
      ]);
      setSettings(nextSettings);
      setDrafts(nextSettings.items.map(normalizedDraft));
      setGlobalUpPct(Math.abs(nextSettings.defaultUpPct || 5));
      setGlobalDownPct(Math.abs(nextSettings.defaultDownPct || -4));
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || '보유종목 알림 설정을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [authenticated, refreshChannels]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const pushReady = browserStatus.supported
    && browserStatus.permission === 'granted'
    && browserStatus.subscribed
    && serverStatus.serverConfigured
    && serverStatus.subscribed
    && serverStatus.subscriptionCount > 0;

  const telegramReady = Boolean(telegram?.linked && telegram?.realtimeAlertsActive);
  const anyChannelReady = pushReady || telegramReady;

  const updateDraft = useCallback((ticker: string, patch: Partial<PortfolioThresholdItem>) => {
    setDrafts((current) => current.map((item) => (
      item.ticker === ticker ? { ...item, ...patch } : item
    )));
  }, []);

  const saveAllRows = useCallback(async () => {
    const invalid = drafts.find((item) => !validPct(Math.abs(item.upPct)) || !validPct(Math.abs(item.downPct)));
    if (invalid) {
      setMessage('상승·하락 기준은 각각 0.1% 이상 30% 이하로 입력해 주세요.');
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      const next = await updatePortfolioThresholdSettings(
        drafts.map((item) => ({
          ticker: item.ticker,
          upPct: Math.abs(Number(item.upPct)),
          downPct: -Math.abs(Number(item.downPct)),
          enabled: item.enabled !== false,
        })),
      );
      setSettings(next);
      setDrafts(next.items.map(normalizedDraft));
      setMessage('보유종목별 상승·하락 알림 기준을 저장했습니다.');
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || '알림 기준을 저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  }, [drafts]);

  const applyGlobal = useCallback(async () => {
    if (!validPct(globalUpPct) || !validPct(globalDownPct)) {
      setMessage('전체 적용 기준은 0.1% 이상 30% 이하로 입력해 주세요.');
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      const next = await updateAllPortfolioThresholdSettings({
        upPct: Math.abs(globalUpPct),
        downPct: -Math.abs(globalDownPct),
        enabled: true,
      });
      setSettings(next);
      setDrafts(next.items.map(normalizedDraft));
      setMessage(`전체 보유종목에 +${Math.abs(globalUpPct)}% / -${Math.abs(globalDownPct)}% 기준을 적용했습니다.`);
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || '전체 알림 기준을 저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  }, [globalDownPct, globalUpPct]);

  const enablePush = useCallback(async () => {
    setChannelAction('push');
    setMessage('');
    try {
      await enableWebPush(serverStatus.vapidPublicKey);
      await refreshChannels();
      setMessage('웹푸시 보유종목 알림을 켰습니다.');
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || '웹푸시를 켜지 못했습니다.');
    } finally {
      setChannelAction('');
    }
  }, [refreshChannels, serverStatus.vapidPublicKey]);

  const disablePush = useCallback(async () => {
    setChannelAction('push');
    setMessage('');
    try {
      await disableWebPush();
      await refreshChannels();
      setMessage('이 브라우저의 웹푸시 보유종목 알림을 껐습니다.');
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || '웹푸시를 끄지 못했습니다.');
    } finally {
      setChannelAction('');
    }
  }, [refreshChannels]);

  const toggleTelegram = useCallback(async (active: boolean) => {
    setChannelAction('telegram');
    setMessage('');
    try {
      const next = await setTelegramRealtimeActive(active);
      setTelegram(next);
      setMessage(active
        ? 'Telegram 보유종목 변동 알림을 켰습니다.'
        : 'Telegram 보유종목 변동 알림을 껐습니다.');
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || 'Telegram 알림 설정을 변경하지 못했습니다.');
    } finally {
      setChannelAction('');
    }
  }, []);

  const connectTelegram = useCallback(async () => {
    setChannelAction('telegram-connect');
    setMessage('');
    try {
      const next = await beginTelegramRealtimeConnection();
      setTelegram(next);
      const deepLink = String(next?.deepLink ?? '').trim();
      setTelegramDeepLink(deepLink);
      if (next?.linked) {
        const activated = await setTelegramRealtimeActive(true);
        setTelegram(activated);
        setMessage('Telegram 연결을 확인하고 보유종목 변동 알림을 켰습니다.');
      } else {
        setMessage('Telegram에서 Start를 한 번 누르면 연결됩니다. 연결 후 이 화면에서 실시간 알림을 켜 주세요.');
      }
    } catch (error: any) {
      setMessage(error?.response?.data?.message || error?.message || 'Telegram 연결을 시작하지 못했습니다.');
    } finally {
      setChannelAction('');
    }
  }, []);

  const titleSummary = useMemo(() => {
    if (!settings?.holdingCount) return '보유종목 없음';
    if (!anyChannelReady) return `${settings.holdingCount}개 감시 · 수신 채널 꺼짐`;
    return `${settings.holdingCount}개 감시 · 알림 수신 중`;
  }, [anyChannelReady, settings?.holdingCount]);

  if (!authenticated) return null;

  return (
    <Box sx={{ display: 'grid', gap: 1.1 }}>
      <Paper elevation={0} sx={{ p: { xs: 1.2, md: 1.4 }, borderRadius: 3, background: PANEL, border: BORDER }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Box>
            <Typography sx={{ color: TXT, fontSize: 14, fontWeight: 950 }}>보유종목 변동 알림</Typography>
            <Typography sx={{ color: SUB, fontSize: 11.6, mt: 0.35, lineHeight: 1.6 }}>
              시장 전체·스캐너 변동을 푸시하지 않습니다. 포트폴리오에 등록한 종목이 사용자가 정한 당일 상승·하락 기준에 처음 진입할 때만 알려드립니다.
            </Typography>
          </Box>
          <Chip
            size="small"
            label={titleSummary}
            sx={{ fontWeight: 900, bgcolor: anyChannelReady ? '#edf8f1' : '#fff8e8', color: anyChannelReady ? GOOD : AMBER }}
          />
        </Box>

        {loading ? <Box sx={{ py: 2, display: 'grid', placeItems: 'center' }}><CircularProgress size={22} /></Box> : null}

        {!loading && settings && settings.holdingCount <= 0 ? (
          <Typography sx={{ color: SUB, fontSize: 12.2, mt: 1.1 }}>
            먼저 포트폴리오 탭에 보유종목을 등록해 주세요. 보유하지 않은 종목에는 실시간 변동 알림을 만들지 않습니다.
          </Typography>
        ) : null}

        {!loading && settings && settings.holdingCount > 0 ? (
          <>
            <Box sx={{ mt: 1.2, p: 1.1, borderRadius: 2.2, bgcolor: CARD, border: BORDER }}>
              <Typography sx={{ color: TXT, fontSize: 12.2, fontWeight: 950 }}>전체 보유종목 기준 빠른 적용</Typography>
              <Typography sx={{ color: SUB2, fontSize: 10.8, mt: 0.3 }}>당일 전일종가 대비 등락률 기준입니다.</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.8} sx={{ mt: 0.9, alignItems: { sm: 'center' } }}>
                <TextField
                  size="small"
                  type="number"
                  label="상승 +%"
                  value={globalUpPct}
                  inputProps={{ min: 0.1, max: 30, step: 0.1 }}
                  onChange={(e) => setGlobalUpPct(Math.abs(Number(e.target.value)))}
                  sx={{ width: { xs: '100%', sm: 130 } }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="하락 -%"
                  value={globalDownPct}
                  inputProps={{ min: 0.1, max: 30, step: 0.1 }}
                  onChange={(e) => setGlobalDownPct(Math.abs(Number(e.target.value)))}
                  sx={{ width: { xs: '100%', sm: 130 } }}
                />
                <Button variant="contained" disabled={saving} onClick={() => void applyGlobal()} sx={{ bgcolor: BLUE, fontWeight: 950, textTransform: 'none' }}>
                  전체 적용
                </Button>
              </Stack>
            </Box>

            <Box sx={{ mt: 1.1, display: 'grid', gap: 0.7 }}>
              {drafts.map((item) => (
                <Paper key={item.ticker} elevation={0} sx={{ p: 1, borderRadius: 2.1, bgcolor: item.enabled ? '#fff' : '#f5f6f7', border: BORDER }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(160px,1fr) 115px 115px auto' }, gap: 0.8, alignItems: 'center' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: TXT, fontSize: 12.4, fontWeight: 950 }} noWrap>{item.tickerName || item.ticker}</Typography>
                      <Typography sx={{ color: SUB2, fontSize: 10.5 }}>{item.ticker}</Typography>
                    </Box>
                    <TextField
                      size="small"
                      type="number"
                      label="상승 +%"
                      value={Math.abs(item.upPct)}
                      inputProps={{ min: 0.1, max: 30, step: 0.1 }}
                      onChange={(e) => updateDraft(item.ticker, { upPct: Math.abs(Number(e.target.value)) })}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="하락 -%"
                      value={Math.abs(item.downPct)}
                      inputProps={{ min: 0.1, max: 30, step: 0.1 }}
                      onChange={(e) => updateDraft(item.ticker, { downPct: -Math.abs(Number(e.target.value)) })}
                    />
                    <Stack direction="row" spacing={0.4} alignItems="center" justifyContent={{ xs: 'space-between', md: 'flex-end' }}>
                      <Typography sx={{ color: item.enabled ? GOOD : SUB2, fontSize: 10.8, fontWeight: 900 }}>{item.enabled ? '감시 ON' : '감시 OFF'}</Typography>
                      <Switch checked={item.enabled} onChange={(e) => updateDraft(item.ticker, { enabled: e.target.checked })} />
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" disabled={saving} onClick={() => void saveAllRows()} sx={{ bgcolor: BLUE, fontWeight: 950, textTransform: 'none' }}>
                {saving ? '저장 중...' : '종목별 기준 저장'}
              </Button>
            </Box>
          </>
        ) : null}
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 1.2, md: 1.4 }, borderRadius: 3, background: PANEL, border: BORDER }}>
        <Typography sx={{ color: TXT, fontSize: 13.5, fontWeight: 950 }}>알림 수신 채널</Typography>
        <Typography sx={{ color: SUB, fontSize: 11.4, mt: 0.35, lineHeight: 1.6 }}>
          여기서 끄면 해당 채널의 보유종목 변동 알림이 중지됩니다. 다시 켜는 위치도 마이페이지 → 알림입니다.
        </Typography>

        <Divider sx={{ my: 1.1 }} />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
          <Paper elevation={0} sx={{ p: 1, borderRadius: 2.2, bgcolor: CARD, border: BORDER }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Box>
                <Typography sx={{ color: TXT, fontSize: 12.2, fontWeight: 950 }}>웹푸시</Typography>
                <Typography sx={{ color: SUB2, fontSize: 10.6, mt: 0.2 }}>
                  {pushReady ? '이 브라우저에서 수신 중' : browserStatus.permission === 'denied' ? '브라우저에서 차단됨' : '수신 꺼짐'}
                </Typography>
              </Box>
              <Chip size="small" label={pushReady ? 'ON' : 'OFF'} sx={{ fontWeight: 950, color: pushReady ? GOOD : SUB2 }} />
            </Stack>
            <Box sx={{ mt: 0.9 }}>
              {pushReady ? (
                <Button size="small" variant="outlined" disabled={channelAction === 'push'} onClick={() => void disablePush()} sx={{ fontWeight: 900, textTransform: 'none' }}>웹푸시 끄기</Button>
              ) : (
                <Button
                  size="small"
                  variant="contained"
                  disabled={channelAction === 'push' || !serverStatus.serverConfigured || browserStatus.permission === 'denied' || !browserStatus.supported}
                  onClick={() => void enablePush()}
                  sx={{ bgcolor: BLUE, fontWeight: 900, textTransform: 'none' }}
                >
                  웹푸시 켜기
                </Button>
              )}
            </Box>
            {browserStatus.permission === 'denied' ? (
              <Typography sx={{ color: AMBER, fontSize: 10.5, mt: 0.7, lineHeight: 1.5 }}>
                브라우저 사이트 설정에서 알림 권한을 허용한 뒤 이 화면에서 다시 켜 주세요.
              </Typography>
            ) : null}
          </Paper>

          <Paper elevation={0} sx={{ p: 1, borderRadius: 2.2, bgcolor: CARD, border: BORDER }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Box>
                <Typography sx={{ color: TXT, fontSize: 12.2, fontWeight: 950 }}>Telegram</Typography>
                <Typography sx={{ color: SUB2, fontSize: 10.6, mt: 0.2 }}>
                  {telegram?.linked ? (telegramReady ? '연결됨 · 보유종목 알림 ON' : '연결됨 · 알림 OFF') : '계정 연결 필요'}
                </Typography>
              </Box>
              <Chip size="small" label={telegramReady ? 'ON' : 'OFF'} sx={{ fontWeight: 950, color: telegramReady ? GOOD : SUB2 }} />
            </Stack>
            <Box sx={{ mt: 0.9, display: 'flex', gap: 0.7, flexWrap: 'wrap' }}>
              {telegram?.linked ? (
                <Button size="small" variant={telegramReady ? 'outlined' : 'contained'} disabled={channelAction === 'telegram'} onClick={() => void toggleTelegram(!telegramReady)} sx={{ bgcolor: telegramReady ? undefined : BLUE, fontWeight: 900, textTransform: 'none' }}>
                  {telegramReady ? 'Telegram 끄기' : 'Telegram 켜기'}
                </Button>
              ) : (
                <Button size="small" variant="contained" disabled={channelAction === 'telegram-connect'} onClick={() => void connectTelegram()} sx={{ bgcolor: BLUE, fontWeight: 900, textTransform: 'none' }}>
                  Telegram 연결
                </Button>
              )}
              {telegramDeepLink ? (
                <Button size="small" component="a" href={telegramDeepLink} target="_blank" rel="noreferrer" variant="outlined" sx={{ fontWeight: 900, textTransform: 'none' }}>
                  Telegram에서 Start
                </Button>
              ) : null}
            </Box>
            {!telegram?.linked ? (
              <Typography sx={{ color: SUB, fontSize: 10.5, mt: 0.7, lineHeight: 1.5 }}>
                Telegram은 보안상 최초 연결 시 Telegram 앱에서 Start를 한 번 눌러야 합니다. Chat ID를 직접 입력하지 않습니다.
              </Typography>
            ) : null}
          </Paper>
        </Box>
      </Paper>

      {message ? (
        <Paper elevation={0} sx={{ p: 1, borderRadius: 2, bgcolor: '#f8fbfd', border: BORDER }}>
          <Typography sx={{ color: SUB, fontSize: 11.3, lineHeight: 1.55 }}>{message}</Typography>
        </Paper>
      ) : null}
    </Box>
  );
}
